/**
 * iPhone-checklist voor pushmeldingen (sessie 4, 09-09-2026).
 *
 * De stappen stonden alleen in `docs/PUSHMELDINGEN.md`. Dat helpt niet
 * wanneer je met de telefoon in je hand op de profielpagina staat, dus
 * ze staan nu hier — afvinkbaar, met de stand in localStorage zodat het
 * per toestel klopt.
 *
 * De eisen zijn op 09-09-2026 nagelopen tegen de actuele
 * WebKit-documentatie en sinds iOS 16.4 onveranderd: web push werkt op
 * iPhone en iPad **alleen** vanuit een web-app op het beginscherm (het
 * manifest staat op `display: standalone`), en de permissievraag moet
 * uit een directe handeling van de gebruiker komen — de schakelaar
 * hierboven is die handeling.
 *
 * Twee stappen zijn nieuw ten opzichte van de oude handleiding, en
 * precies de twee die meldingen laten verdwijnen zonder dat er iets
 * kapot is: iOS zet meldingen per web-app apart in Instellingen, en een
 * actieve Focus (of Niet storen) onderdrukt ze zonder waarschuwing.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Smartphone } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { draaitStandalone, isIos } from "@/lib/push";

/** Vijf stappen; de sleutels staan in `profiel.ios_stap_<n>`. */
const STAPPEN = [1, 2, 3, 4, 5] as const;

const KEY = "ks.iosChecklist";

function leesAfgevinkt(): number[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const waarde = JSON.parse(raw);
    return Array.isArray(waarde) ? waarde.filter(Number.isInteger) : [];
  } catch {
    return [];
  }
}

function schrijfAfgevinkt(stappen: number[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(stappen));
  } catch {
    /* private mode of volle quota: de stand geldt alleen deze sessie */
  }
}

export function IosChecklist() {
  const { t } = useTranslation();
  const [afgevinkt, setAfgevinkt] = useState<number[]>(leesAfgevinkt);

  const wissel = useCallback((stap: number, aan: boolean) => {
    setAfgevinkt((huidig) => {
      const volgend = aan
        ? [...new Set([...huidig, stap])]
        : huidig.filter((n) => n !== stap);
      schrijfAfgevinkt(volgend);
      return volgend;
    });
  }, []);

  // Alleen tonen waar hij nut heeft: op een iPhone/iPad. Draait het
  // portaal daar al als web-app op het beginscherm, dan is stap 1 en 2
  // gelukt — dat zegt het kader er dan ook bij.
  if (!isIos()) return null;
  const alsApp = draaitStandalone();

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-brand-cyan-soft text-brand-cyan-dark grid place-items-center">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-[15px] font-semibold">
              {t("profiel.ios_titel")}
            </h2>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              {t("profiel.ios_uitleg")}
            </p>
          </div>
        </div>

        {alsApp && (
          <p className="text-[12.5px] inline-flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1.5">
            <Check className="h-3.5 w-3.5" />
            {t("profiel.ios_al_als_app")}
          </p>
        )}

        <ol className="space-y-2.5" data-testid="ios-checklist">
          {STAPPEN.map((stap) => (
            <li key={stap} className="flex items-start gap-2.5">
              <Checkbox
                id={`ios-stap-${stap}`}
                className="mt-0.5"
                checked={afgevinkt.includes(stap)}
                onCheckedChange={(aan) => wissel(stap, aan === true)}
              />
              <Label
                htmlFor={`ios-stap-${stap}`}
                className="text-[13.5px] font-normal leading-snug"
              >
                {t(`profiel.ios_stap_${stap}`)}
              </Label>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
